import { action, computed, makeObservable, observable, runInAction } from 'mobx'

import Localization from '~/client/src/shared/localization/LocalizationManager'
import SmartFormImportManagerStore from '~/client/src/shared/stores/domain/SmartFormImportManager.store'
import WorkflowCategoriesStore from '~/client/src/shared/stores/domain/WorkflowCategories.store'
import {
  validateFileDimensions,
  validateFileSize,
} from '~/client/src/shared/utils/fileValidationHelper'
import { MEGABYTE } from '~/client/src/shared/utils/usefulStrings'

import { IFormGeneralTabDropdownOption } from '../../../FormTypeConfigurator/components/GeneralFormConfigurator/components/FormGeneralOptionsDropdown'

const MAX_WIDTH = 20000
const MAX_HEIGHT = 20000
const MAX_FILE_SIZE = 50 * 1024 * 1024 // 50MB
const ALLOWED_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/jpg',
  'image/png',
]
export default class SmartFormImportStore {
  @observable
  public file: File = null

  @observable
  public workflowName: string = ''

  @observable
  public categoryId: string | null = null

  @observable
  public isDragOver: boolean = false

  @observable
  public fileValidationMessage: string | null = null

  public constructor(
    private readonly workflowCategoriesStore: WorkflowCategoriesStore,
    private readonly smartFormImportManagerStore: SmartFormImportManagerStore,
  ) {
    makeObservable(this)
  }

  @computed
  public get isImporting(): boolean {
    return this.smartFormImportManagerStore.isImporting
  }

  @computed
  public get categoriesOptions(): IFormGeneralTabDropdownOption[] {
    return this.workflowCategoriesStore.activeCategories.map(category => ({
      id: category.id,
      name: category.name,
    }))
  }

  public handleFileSelect = async (file: File): Promise<void> => {
    await this.assignFile(file)
  }

  @action
  public handleRemoveFile = (): void => {
    this.file = null
  }

  @action
  public handleWorkflowNameChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    this.workflowName = event.target.value
  }

  @action
  public handleCategoryChange = (categoryId: string): void => {
    this.categoryId = categoryId
  }

  @action
  public handleDragEnter = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault()
    event.stopPropagation()
    this.isDragOver = true
  }

  @action
  public handleDragLeave = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault()
    event.stopPropagation()
    if (!event.currentTarget.contains(event.relatedTarget as Node)) {
      this.isDragOver = false
    }
  }

  @action
  public handleDrop = async (
    event: React.DragEvent<HTMLDivElement>,
  ): Promise<void> => {
    event.preventDefault()
    event.stopPropagation()
    this.isDragOver = false

    const file = event.dataTransfer.files[0]

    if (!ALLOWED_TYPES.includes(file.type)) {
      return
    }

    await this.assignFile(file)
  }

  @computed
  public get areAllFieldsValid(): boolean {
    return !!this.file && !!this.workflowName && !!this.categoryId
  }

  public importFile = async (): Promise<void> => {
    await this.smartFormImportManagerStore.startImport(
      this.file,
      this.workflowName,
      this.categoryId,
    )
  }

  private async assignFile(file: File) {
    if (!file) {
      runInAction(() => {
        this.file = null
        this.fileValidationMessage = null
      })
      return
    }

    const [isValid, message] = await this.validateFile(file)

    if (isValid) {
      runInAction(() => {
        this.file = file
        this.fileValidationMessage = null
      })
    } else {
      runInAction(() => {
        this.file = null
        this.fileValidationMessage = message
      })
    }
  }

  public async validateFile(file: File): Promise<[boolean, string]> {
    if (
      file.type.startsWith('image/') &&
      !(await validateFileDimensions(file, MAX_WIDTH, MAX_HEIGHT))
    ) {
      return [
        false,
        Localization.translator.imageDimensionsCanNotBeGreaterThan(
          MAX_WIDTH,
          MAX_HEIGHT,
        ),
      ]
    }
    if (!validateFileSize(file, MAX_FILE_SIZE)) {
      return [
        false,
        Localization.translator.fileSizeCanNotBeGreaterThan(
          `${MAX_FILE_SIZE / 1024 / 1024}${MEGABYTE}`,
        ),
      ]
    }
    return [true, null]
  }
}
